import type { PersonaGiuridicaDTO } from '@framework/api-client';
import { getCountryName } from '../../anpr/anprCountries';
import type { ILegalEntitySessionData } from './ISessionData';

/**
 * Mapping per costruire un oggetto `ILegalEntitySessionData` a partire dai dati PG
 * provenienti dall'asserzione SPID/CIE (`PersonaGiuridicaDTO`).
 */
export function toLegalEntitySessionData(
  legalEntity: PersonaGiuridicaDTO | null | undefined,
): ILegalEntitySessionData | undefined {
  if (!legalEntity) return undefined;

  return {
    companyName: legalEntity.ragioneSociale,
    vatNumber: legalEntity.partitaIva,
    address: legalEntity.indirizzo,
    postalCode: legalEntity.cap,
    // `paeseSedeLegale` è un codice ANPR (es. '401'): lo decodifichiamo nel nome
    // del paese (es. 'ALGERIA'), ripiegando sul codice grezzo se non in anagrafica.
    country:
      getCountryName(legalEntity.paeseSedeLegale) ??
      legalEntity.paeseSedeLegale,
    province: legalEntity.provincia,
    municipality: legalEntity.comune,
    peo: legalEntity.peo,
    pec: legalEntity.pec,
    phone: legalEntity.telefono,
    isAccredited: legalEntity.flAccreditata,
  };
}

/**
 * Costruisce la PG "unificata" (`mergedLegalEntity`) combinando i due set di
 * dati della stessa azienda che possono coesistere in sessione:
 *
 * - `spid`: dati PG provenienti dall'asserzione SPID/CIE
 *   (`datiAnagraficiAggiuntivi.personaGiuridica`);
 * - `selectedProfile`: dati PG presenti a DB sul profilo selezionato
 *   (`profiloSelezionato.personaGiuridica`).
 */
export function mergeLegalEntitySessionData(
  spid: ILegalEntitySessionData | undefined,
  selectedProfile: ILegalEntitySessionData | undefined,
): ILegalEntitySessionData | undefined {
  if (!spid && !selectedProfile) return undefined;

  return {
    companyName: spid?.companyName ?? selectedProfile?.companyName,
    vatNumber: spid?.vatNumber ?? selectedProfile?.vatNumber,
    address: spid?.address ?? selectedProfile?.address,
    postalCode: spid?.postalCode ?? selectedProfile?.postalCode,
    country: spid?.country ?? selectedProfile?.country,
    province: spid?.province ?? selectedProfile?.province,
    municipality: spid?.municipality ?? selectedProfile?.municipality,
    peo: spid?.peo ?? selectedProfile?.peo,
    pec: spid?.pec ?? selectedProfile?.pec,
    phone: spid?.phone ?? selectedProfile?.phone,
    isAccredited: spid?.isAccredited ?? selectedProfile?.isAccredited,
  };
}
